import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import DataGrid from '../../../components/DataGrid';
import Forms from '../../../components/Forms';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import {useModal} from '../../../contexts/ModalContext';
import {ReactComponent as DeleteIcon} from '../../../assets/images/delete.svg';
import {ReactComponent as EditIcon} from '../../../assets/images/edit.svg';
import BlogModal from './BlogModal';

const Blog = () => {
    const {t} = useTranslation();
    const {openModal, confirm} = useModal();

    const [posts, setPosts] = useState([]);

    // filtry
    const [query, setQuery] = useState('');
    const [onlyActive, setOnlyActive] = useState(false);

    const fetchPosts = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.blog.list);
            setPosts(res.data || []);
        } catch (e) {
            console.error('Failed to fetch blog posts:', e);
        }
    }, []);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    const openBlogModal = (post) => {
        openModal(
            BlogModal,
            {post, onPostsUpdate: fetchPosts},
            {title: post ? t('blog.edit') : t('blog.add')}
        );
    };

    const confirmDelete = async (post) => {
        const ok = await confirm(t('blog.confirmDeletion', {name: post.title}));
        if (ok === true) {
            try {
                await API.delete(API_ENDPOINTS.blog.delete(post._id));
                setPosts(prev => prev.filter(p => p._id !== post._id));
            } catch (e) {
                console.error('Failed to delete blog post:', e);
            }
        }
    };

    const queryLc = query.trim().toLowerCase();
    const rows = useMemo(() => {
        return (posts || []).filter(p => {
            if (onlyActive && !p.active) return false;
            if (queryLc) {
                const hay = `${p.title || ''} ${(p.text || '').replace(/\s+/g, ' ')}`.toLowerCase();
                if (!hay.includes(queryLc)) return false;
            }
            return true;
        });
    }, [posts, onlyActive, queryLc]);

    const columns = [
        {
            key: 'title',
            name: t('blog.title'),
            minWidth: 120,
            renderCell: ({row}) => (
                <div className="flex items-center gap-2 w-full">
                    <span className="min-w-0 flex-1 truncate" title={row.title}>
                        {row.title}
                    </span>
                    <div className="flex items-center gap-2 flex-none shrink-0">
                        <DeleteIcon
                            className="w-4 h-4 cursor-pointer fill-red-700"
                            onClick={() => confirmDelete(row)}
                        />
                        <EditIcon
                            className="w-4 h-4 cursor-pointer text-blue-800"
                            onClick={() => openBlogModal(row)}
                        />
                    </div>
                </div>
            ),
        },
        {
            key: 'slug',
            name: t('blog.slug'),
            renderCell: ({row}) => row.slug || '-'
        },
        {
            key: 'text',
            name: t('blog.text'),
            renderCell: ({row}) => `${String(row.text || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 100)}…`
        },
        {
            key: 'active',
            name: t('blog.active'),
            renderCell: ({row}) => row.active ? t('yes') : t('no')
        },
        {
            key: 'publishedAt',
            name: t('blog.publishedAt'),
            renderCell: ({row}) => row.publishedAt ? moment(row.publishedAt).format('DD.MM.YYYY HH:mm') : '-'
        },
        {
            key: 'createdAt',
            name: t('createdAt'),
            width: '18%',
            renderCell: ({row}) => row.createdAt ? moment(row.createdAt).format('DD.MM.YYYY HH:mm') : '-'
        }
    ].map(c => ({...c, resizable: true}));

    return (
        <div>
            <div className="flex items-start justify-between mb-4">
                <Forms.Button label={t('blog.add')} onClick={() => openBlogModal()}/>

                <div className="flex gap-6 items-end">
                    <Forms.Text
                        label={t('search')}
                        value={query}
                        onChange={setQuery}
                    />

                    <label className="inline-flex items-center gap-2">
                        <input
                            type="checkbox"
                            className="cursor-pointer"
                            checked={onlyActive}
                            onChange={(e) => setOnlyActive(e.target.checked)}
                        />
                        <span>{t('blog.onlyActive')}</span>
                    </label>

                    {(onlyActive || query) && (
                        <Forms.Button variant="secondary" onClick={() => {
                            setOnlyActive(false);
                            setQuery('');
                        }} label={t('clearFilters')}/>
                    )}
                </div>
            </div>

            <DataGrid rows={rows} columns={columns}/>
        </div>
    );
};

export default Blog;
